import { defineStore } from 'pinia'
import generalService from '@/services/general.service'
import investorService from '@/services/investor.service'
import localStorageService from '@/core/localstorage.service'
import { getBrowserLocale } from '@/core/helper-functions'
import { resolveLocale } from '@/locales/locales'
import router from '@/router'
import { ElNotification } from 'element-plus'
import authService from '@/services/auth.service'
import { useAuthStore } from './auth'
import { useCompanyStore } from './company'
import { useInvestorStore } from './investor'

export const useGeneralStore = defineStore('general', {
  state: () => ({
    windowWidth: 0,
    loading: false,
    userData: null,
    countries: null,
    provinces: null,
    activities: null,
    popupRead: false,
    notifications: [],
    locale: localStorageService.getItem('locale') || getBrowserLocale().slice(0, 2),
    isIdle: false
  }),

  getters: {
    /* Always route/redirect through this rather than the raw `locale` state -
       that is seeded from localStorage or the browser and may be unsupported. */
    currentLocale: state => resolveLocale(state.locale),

    isSkyline: state => {
      if (state.userData) return state.userData.skyline
    },

    isMobile: state => state.windowWidth < 768,

    isTabletDown: state => state.windowWidth < 1200
  },

  actions: {
    setUserData (data) {
      this.userData = { ...this.userData, ...data }
    },

    setWindowWidth (value) {
      this.windowWidth = value
    },

    setLoading (value) {
      this.loading = value
    },

    setCountries (value) {
      this.countries = value
    },

    setProvinces (value) {
      this.provinces = value
    },

    setActivities (value) {
      this.activities = value
    },

    setNotifications (value) {
      this.notifications = value
    },

    setPopupRead (value) {
      this.popupRead = value
    },

    changeDeviceProperty (val) {
      this.setWindowWidth(val)
    },

    setPopupReadToRead () {
      this.setPopupRead(true)
    },

    setIdle (value) {
      this.isIdle = value
    },

    async getGlobalData () {
      try {
        await this.getUserData()

        const response = await investorService.getInvestors()

        const investors = Object.values(response.data.data)

        if (!investors || !investors.length) {
          ElNotification.error({
            title: 'Error',
            message: 'Investors not found',
            position: 'bottom-right'
          })
          throw Error('Investors not found')
        }

        const investorStore = useInvestorStore()
        investorStore.setInvestors(investors)
        investorStore.setCurrentInvestor(investorService.validateCurrentInvestor(investors))

        this.getProvinces({
          search: '',
          countryId: investors[0].country_id
        })
        this.getCountries()
        this.getNotifications()
        return Promise.resolve(investors)
      } catch (err) {
        const companyStore = useCompanyStore()

        /* Drop the token as well as redirecting. Leaving it in localStorage lets
           the route guard wave the user straight back through on the next
           navigation, which loops them between here and the login page. */
        useAuthStore().removeToken()

        router.push({ ...companyStore.baseRouteConfig, name: 'login' }).catch(() => {})
        return Promise.reject(err)
      }
    },

    async getUserData () {
      try {
        const { data } = await generalService.getUserData()
        this.setUserData(data)
      } catch (err) {
        return Promise.reject(err)
      }
    },

    async getCountries (value) {
      try {
        const { data } = await generalService.getCountries(value)
        this.setCountries(data.data)
        return Promise.resolve(data.data)
      } catch (err) {
        return Promise.reject(err)
      }
    },

    async getProvinces (value) {
      try {
        const { data } = await generalService.getProvinces(value)
        this.setProvinces(data.data)
        return Promise.resolve(data.data)
      } catch (err) {
        return Promise.reject(err)
      }
    },

    async getActivities () {
      try {
        const { data } = await generalService.getActivities()
        this.setActivities(data.data)
        return Promise.resolve(data.data)
      } catch (err) {
        return Promise.reject(err)
      }
    },

    async getNotifications () {
      try {
        const { data } = await generalService.getNotifications()
        this.setNotifications(data.data)
        return Promise.resolve(data.data)
      } catch (err) {
        return Promise.reject(err)
      }
    },

    async toggle2FA (data) {
      try {
        const { data: response } = await authService.toggle2FA(data)
        this.setUserData({ verify_login: response.verify_login })
        return response
      } catch (err) {
        return Promise.reject(err)
      }
    }
  }
})
